import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categoryLinks } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const langs = [
    { code: 'pt', label: 'PT', full: 'Português' },
    { code: 'en', label: 'EN', full: 'English' },
    { code: 'de', label: 'DE', full: 'Deutsch' },
  ];

  const currentLang = langs.find((l) => l.code === i18n.language) || langs[0];

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-sm border-b border-warm-200'
          : 'bg-white/90 backdrop-blur-md border-b border-warm-100'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl md:text-2xl font-extrabold text-warm-900">
              CSS<span className="text-brand-600">Spreadsheet</span>BR
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            <Link to="/" className="text-sm font-semibold text-warm-800 hover:text-brand-600 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>

            <Link to="/pt/articles" className="text-sm font-semibold text-warm-800 hover:text-brand-600 transition-colors whitespace-nowrap">
              {t('nav.shoppingGuide')}
            </Link>

            <div className="relative">
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-warm-800 hover:text-brand-600 transition-colors whitespace-nowrap"
              >
                {t('categories.dropdownLabel')}
                <i className="ri-arrow-down-s-line text-xs" />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-warm-200 shadow-lg overflow-hidden">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-warm-700 hover:bg-warm-50 hover:text-brand-600 transition-colors border-b border-warm-100 last:border-0"
                      onClick={() => setCatOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/pt/articles/cssbuy-spreadsheet-guide" className="text-sm font-semibold text-warm-800 hover:text-brand-600 transition-colors whitespace-nowrap">
              {t('nav.spreadsheet')}
            </Link>
            <Link to="/pt/articles" className="text-sm font-semibold text-warm-800 hover:text-brand-600 transition-colors whitespace-nowrap">
              {t('nav.articles')}
            </Link>
            <Link to="/about" className="text-sm font-semibold text-warm-800 hover:text-brand-600 transition-colors whitespace-nowrap">
              {t('nav.about')}
            </Link>

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-warm-300 text-sm font-semibold text-warm-800 hover:border-brand-400 transition-colors"
              >
                <i className="ri-global-line text-sm" />
                {currentLang.label}
                <i className="ri-arrow-down-s-line text-xs" />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-xl border border-warm-200 shadow-lg overflow-hidden">
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLang(l.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-warm-100 last:border-0 ${
                        i18n.language === l.code
                          ? 'bg-brand-50 text-brand-700 font-medium'
                          : 'text-warm-700 hover:bg-warm-50'
                      }`}
                    >
                      {l.full}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            className="lg:hidden p-2 text-warm-900"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-warm-200 px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="space-y-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-semibold text-warm-900 hover:bg-warm-50">
              {t('nav.home')}
            </Link>
            <Link to="/pt/articles" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-semibold text-warm-900 hover:bg-warm-50">
              {t('nav.shoppingGuide')}
            </Link>
            <Link to="/pt/articles/cssbuy-spreadsheet-guide" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-semibold text-warm-900 hover:bg-warm-50">
              {t('nav.spreadsheet')}
            </Link>
            <Link to="/pt/articles" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-semibold text-warm-900 hover:bg-warm-50">
              {t('nav.articles')}
            </Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-semibold text-warm-900 hover:bg-warm-50">
              {t('nav.about')}
            </Link>
          </div>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-base font-semibold text-warm-900 bg-warm-50 mt-3"
          >
            {t('categories.dropdownLabel')}
            <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`} />
          </button>

          {mobileCatOpen && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              {categoryLinks.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.href}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-3 py-2.5 rounded-lg text-sm font-medium text-warm-700 bg-white border border-warm-200 hover:border-brand-300 hover:text-brand-600 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <div className="pt-3 mt-3 border-t border-warm-200 flex gap-2 flex-wrap">
            {langs.map((l) => (
              <button
                key={l.code}
                onClick={() => changeLang(l.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  i18n.language === l.code
                    ? 'bg-brand-50 border-brand-300 text-brand-700'
                    : 'border-warm-300 text-warm-700'
                }`}
              >
                {l.full}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}